import axios, { AxiosError } from "axios";
import Cookies from "universal-cookie";
import { refreshAccessToken } from "../services/userService";
import { globalErrorHandler } from "../utilities/globalErrorHandler";


interface ErrorResponseData {
  error?: string;
  message?: string;
  success?: boolean;
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_ENV_URL,
  timeout: 5000000,
  withCredentials: true,
  params: {} // enable adding params later in the config
});

// CSRF token cache with TTL
let csrfToken: string | null = null;
let csrfTokenFetchedAt: number | null = null;
const CSRF_TTL = 15 * 60 * 1000; // 15 minutes

// Refresh token deduplication — refreshPromise is the sole lock.
// Synchronous check-and-set on this variable eliminates the TOCTOU race
// that occurred when multiple 401 responses arrived in the same event loop tick.
let refreshPromise: Promise<string | null> | null = null;

// Queue for requests waiting on token refresh
let failedQueue: Array<{
  resolve: (value?: string | null) => void;
  reject: (error?: unknown) => void;
}> = [];

// Logout flag — prevents token refresh after logout has been initiated
// This guards against race conditions where logout happens while refresh is in progress
let isLoggingOut = false;

// "View Legacy" lens — module flag mirrored to localStorage so the header
// survives reloads (the toggle hard-reloads on flip). The server validates
// entitlement (CT superAdmin/auditor on the REAL user) and only honors safe
// methods; a non-entitled sender is silently filtered as usual, so this is a
// UI preference, not a credential — deliberately NOT cleared in
// resetInterceptorState.
export const VIEW_LEGACY_STORAGE_KEY = "eip.viewLegacy";

let viewLegacy = false;
try {
  viewLegacy = localStorage.getItem(VIEW_LEGACY_STORAGE_KEY) === "1";
} catch {
  // storage unavailable (tests/SSR) — lens stays off
}

export function setViewLegacy(value: boolean) {
  viewLegacy = value;
  try {
    if (value) {
      localStorage.setItem(VIEW_LEGACY_STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(VIEW_LEGACY_STORAGE_KEY);
    }
  } catch {
    // storage unavailable — in-memory flag still applies for this session
  }
}

export function getViewLegacy(): boolean {
  return viewLegacy;
}

// Process all queued requests after token refresh
const processQueue = (error: unknown = null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Reset all module-level state (must be called on logout to prevent credential bleed)
export function resetInterceptorState() {
  csrfToken = null;
  csrfTokenFetchedAt = null;
  refreshPromise = null;
  failedQueue = [];
  isLoggingOut = false; // Reset on interceptor state clear
}

// Set logout flag to prevent token refresh during logout
export function setLoggingOut(value: boolean) {
  isLoggingOut = value;
}

// Check if logout is in progress
export function getIsLoggingOut(): boolean {
  return isLoggingOut;
}

// Create a separate axios instance for CSRF token fetching (without interceptors)
const csrfAxios = axios.create({
  baseURL: import.meta.env.VITE_ENV_URL,
  withCredentials: true
});

// Fetch CSRF token from server
async function fetchCsrfToken(): Promise<string> {
  const now = Date.now();

  // Return cached token if it exists and hasn't expired
  if (csrfToken && csrfTokenFetchedAt && (now - csrfTokenFetchedAt) < CSRF_TTL) {
    return csrfToken;
  }

  try {
    const response = await csrfAxios.get('/api/auth/csrf-token');
    const token = response.data.csrfToken;
    if (!token) {
      throw new Error('CSRF token not found in response');
    }
    csrfToken = token;
    csrfTokenFetchedAt = now;
    return token;
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
}

// Request interceptor to add CSRF token and proactive token refresh
instance.interceptors.request.use(
  async (config) => {
    // Proactive token refresh - check if access token is about to expire
    const cookie = new Cookies();
    const token = cookie.get('authToken');
    if (token && !refreshPromise) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiresAt = payload.exp * 1000;
        const bufferMs = 60 * 1000; // Refresh if < 1 min remaining

        if (expiresAt - Date.now() < bufferMs) {
          // Kick off background refresh (don't await - let request proceed with current valid token)
          refreshPromise = refreshAccessToken()
            .catch(() => null)
            .finally(() => { refreshPromise = null; });
        }
      } catch {
        // Ignore malformed tokens - will be caught by backend
      }
    }

    // Add CSRF token for state-changing requests
    if (config.method && ['post', 'put', 'delete', 'patch'].includes(config.method.toLowerCase())) {
      const csrfTokenValue = await fetchCsrfToken();
      config.headers['X-CSRF-Token'] = csrfTokenValue;
    }

    // "View Legacy" lens opt-in — sent on every request when toggled on;
    // the server only honors it on safe methods for entitled real users.
    if (viewLegacy) {
      config.headers["X-View-Legacy"] = "1";
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (res) => {
    // success, return res right a way
    return res;
  }, 
  async (err: AxiosError) => {

    const originalRequest = err.config;

    if (err.response && err.response.status === 401 && err.response.data) {

      const errData = err.response.data as ErrorResponseData;
      const resError = errData.message || errData.error;

      if (resError === "Token Expired") {
        const cookie = new Cookies();
        cookie.remove("authToken", { path: '/' });

        if (originalRequest === undefined) {
          throw new Error("originalRequest cannot be undefined when retry request");
        }

        // Add retry limit to prevent infinite loops
        const MAX_RETRIES = 1;
        const retryable = originalRequest as typeof originalRequest & { __retryCount?: number };
        retryable.__retryCount = (retryable.__retryCount || 0) + 1;
        if (retryable.__retryCount > MAX_RETRIES) {
          return Promise.reject(err);
        }

        // Synchronous check-and-set: all concurrent 401 handlers share one refresh promise.
        // No gap between check and assignment, so no TOCTOU race.
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken()
            .then(token => {
              processQueue(null, token);
              return token;
            })
            .catch(err => {
              processQueue(err, null);
              return null;
            })
            .finally(() => { refreshPromise = null; });
        }

        // If this is the first request, await directly
        // If there are already queued requests, add to queue
        const isFirstRequest = failedQueue.length === 0;

        if (isFirstRequest) {
          const newAccessToken = await refreshPromise;

          if (!newAccessToken) {
            cookie.remove("refreshToken", { path: '/' });
            window.location.href = '/login';
            return Promise.reject(err);
          }

          // Retry the original request with new token
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return instance(originalRequest);
        } else {
          // Queue this request and wait for token refresh
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (!token) {
                cookie.remove("refreshToken", { path: '/' });
                window.location.href = '/login';
                return Promise.reject(err);
              }
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }
      }

      if (resError === 'Invalid Token') {
        // Token is tampered/malformed - don't attempt refresh, just logout
        const cookie = new Cookies();
        cookie.remove('authToken', { path: '/' });
        cookie.remove('refreshToken', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(err);
      }

      if (resError === 'Token Blacklisted') {
        const cookie = new Cookies();
        cookie.remove('authToken', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    if (err.response && err.response.status === 403 && err.response.data) {

      const forbiddenData = err.response.data as ErrorResponseData;
      const resError = forbiddenData.message || forbiddenData.error;

      // Handle invalid CSRF token
      if (resError === "invalid csrf token") {
        csrfToken = null; // Clear cached token
        csrfTokenFetchedAt = null;

        if (originalRequest === undefined) {
          throw new Error("originalRequest cannot be undefined when retry request");
        }

        // Retry the original request with a fresh CSRF token
        const newToken = await fetchCsrfToken();
        originalRequest.headers['X-CSRF-Token'] = newToken;
        return instance(originalRequest);
      }

      if (resError === "Refresh token expired" || resError === "Forbidden") {
        const cookie = new Cookies();
        cookie.remove('authToken', { path: '/' });
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    // Handle server errors
    if (err.response && err.response.status === 500 && err.response.data) {

      const serverErrData = err.response.data as ErrorResponseData;
      const resError = serverErrData.error;
      const errorMessage = serverErrData.message || resError;

      // Log detailed error for debugging
      console.error('Server Error (500):', {
        error: resError,
        message: errorMessage,
        url: err.config?.url,
        method: err.config?.method,
        fullResponse: err.response.data
      });

      // handle request server timeout
      if (resError === "Request timed out") {
        globalErrorHandler.emitError(
          'Request Timed Out',
          'The request was timed out. Please try again later or contact support at elevateip@communitech.ca',
          10
        );
      } else {
        // Show specific error message if available, otherwise generic message
        globalErrorHandler.emitError(
          'Server Error',
          errorMessage || 'An error has occured on the server-side. We are working hard to bring it back online, please stay tuned!',
          10
        );
      }
    }

    // Log all unhandled errors for debugging
    console.error('HTTP Request Error:', {
      status: err.response?.status,
      statusText: err.response?.statusText,
      error: (err.response?.data as ErrorResponseData | undefined)?.error,
      message: (err.response?.data as ErrorResponseData | undefined)?.message,
      url: err.config?.url,
      method: err.config?.method
    });

    // [TODO] better handle res error
    return Promise.reject(err);
  }
)

export default instance;